import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Image,
    Icon,
    Flex,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaUserMd, FaCheck } from "react-icons/fa";
import { ChevronRightIcon } from "@chakra-ui/icons";

const strikethrough = keyframes`
    from { width: 0; }
    to { width: 100%; }
`;

const fadeComplete = keyframes`
    from { opacity: 1; transform: scale(1); }
    to { opacity: 0.6; transform: scale(0.98); }
`;

const checkBounce = keyframes`
    0% { transform: scale(0); }
    50% { transform: scale(1.2); }
    100% { transform: scale(1); }
`;

const MemberHomepage = () => {
    const navigate = useNavigate();

    const [goals, setGoals] = useState([
        { id: 1, text: "Complete pre-operative bloodwork", completed: false, setBy: "doctor" },
        { id: 2, text: "Stop blood thinners 7 days before", completed: false, setBy: "doctor" },
        { id: 3, text: "Lose 5kg before surgery", completed: false, setBy: "user" },
        { id: 4, text: "Complete nutrition module", completed: false, setBy: "user" },
        { id: 5, text: "Walk 30 minutes daily", completed: false, setBy: "user" },
    ]);

    const toggleGoal = (goalId: number) => {
        setGoals(goals.map(goal => 
            goal.id === goalId ? { ...goal, completed: !goal.completed } : goal
        ));
    };

    const daysUntilSurgery = 33;
    const nextModule = {
        title: "Nutrition",
        description: "Learn how to optimize your diet for faster healing and better surgical outcomes.",
        image: "https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&q=80"
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box bg="#000000" minHeight="100vh" color="white">
            <Box 
                bg="rgba(0, 0, 0, 0.9)"
                borderBottom="1px solid"
                borderColor="whiteAlpha.100"
                position="sticky"
                top={0}
                zIndex={100}
                backdropFilter="blur(20px)"
            >
                <Container maxW="7xl" px={{ base: 4, md: 6 }}>
                    <Flex 
                        justify="space-between" 
                        align="center" 
                        minH={{ base: "44px", md: "48px" }}
                    >
                        <HStack spacing={2} cursor="pointer" onClick={() => navigate('/')}>
                            <Image 
                                src="/surgready/logo.png" 
                                alt="SurgReady" 
                                h={{ base: "44px", md: "52px" }}
                                objectFit="contain"
                            />
                        </HStack>
                        <HStack spacing={{ base: 4, md: 8 }}>
                            <Button
                                variant="ghost"
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/all-modules')}
                                _hover={{ color: "#4A90E2", bg: "transparent" }}
                            >
                                All Modules
                            </Button>
                            <Button
                                variant="ghost"
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="whiteAlpha.900"
                                _hover={{ color: "#4A90E2", bg: "transparent" }}
                            >
                                Community
                            </Button>
                            <HStack
                                spacing={2}
                                cursor="pointer"
                                onClick={() => navigate('/member-profile')}
                                _hover={{ opacity: 0.8 }}
                                transition="all 0.2s"
                            >
                                <Text
                                    fontSize="xs"
                                    fontWeight="400"
                                    letterSpacing="0.1em"
                                    textTransform="uppercase"
                                    color="whiteAlpha.900"
                                    display={{ base: "none", md: "block" }}
                                >
                                    I am SurgReady
                                </Text>
                                <Box
                                    bg="#4A90E2"
                                    borderRadius="full"
                                    w={8}
                                    h={8}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Icon as={FaUser} w={4} h={4} color="white" />
                                </Box>
                            </HStack>
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Container maxW="7xl" px={{ base: 4, md: 6 }} py={{ base: 12, md: 20 }}>
                <Flex direction={{ base: "column", lg: "row" }} gap={{ base: 12, lg: 16 }}>
                    <VStack align="flex-start" spacing={12} flex="1.2">
                        <VStack align="flex-start" spacing={2}>
                            <HStack align="baseline" spacing={4}>
                                <Text
                                    fontSize={{ base: "6xl", md: "8xl" }}
                                    fontWeight="300"
                                    letterSpacing="-0.03em"
                                    lineHeight="1"
                                    bgGradient="linear(to-r, #4A90E2, #3DA2DD)"
                                    bgClip="text"
                                >
                                    {daysUntilSurgery}
                                </Text>
                                <Text
                                    fontSize={{ base: "xl", md: "2xl" }}
                                    fontWeight="300"
                                    color="whiteAlpha.700"
                                    letterSpacing="-0.01em"
                                >
                                    days until your surgery
                                </Text>
                            </HStack>
                            <Box h="3px" w="120px" bgGradient="linear(to-r, #4A90E2, #3DA2DD)" borderRadius="full" />
                        </VStack>

                        <Box
                            bg="whiteAlpha.50"
                            borderRadius="2xl"
                            overflow="hidden"
                            border="1px solid"
                            borderColor="whiteAlpha.100"
                            w="full"
                            maxW="450px"
                            transition="all 0.3s"
                            _hover={{ borderColor: "whiteAlpha.200", transform: "translateY(-2px)" }}
                        >
                            <Image
                                src={nextModule.image}
                                alt={nextModule.title}
                                w="full"
                                h="200px"
                                objectFit="cover"
                            />
                            <VStack align="flex-start" p={6} spacing={4}>
                                <VStack align="flex-start" spacing={2}>
                                    <Text
                                        fontSize="xs"
                                        fontWeight="500"
                                        letterSpacing="0.15em"
                                        textTransform="uppercase"
                                        color="#4A90E2"
                                    >
                                        Your Next Module
                                    </Text>
                                    <Heading
                                        fontSize={{ base: "2xl", md: "3xl" }}
                                        fontWeight="400"
                                        letterSpacing="-0.02em"
                                    >
                                        {nextModule.title}
                                    </Heading>
                                </VStack>
                                <Text
                                    fontSize="md"
                                    color="whiteAlpha.700"
                                    lineHeight="1.6"
                                >
                                    {nextModule.description}
                                </Text>
                                <Button
                                    size="md"
                                    bg="white"
                                    color="black"
                                    borderRadius="full"
                                    fontWeight="500"
                                    textTransform="uppercase"
                                    letterSpacing="0.1em"
                                    fontSize="xs"
                                    px={6}
                                    _hover={{
                                        bg: "whiteAlpha.900",
                                        transform: "translateY(-1px)",
                                    }}
                                    onClick={() => navigate('/module/3/1')}
                                    transition="all 0.2s"
                                >
                                    Go to Module
                                    <ChevronRightIcon ml={1} />
                                </Button>
                            </VStack>
                        </Box>
                    </VStack>

                    <VStack align="flex-start" spacing={6} flex="1" maxW={{ base: "full", lg: "400px" }}>
                        <Heading
                            fontSize={{ base: "2xl", md: "3xl" }}
                            fontWeight="400"
                            letterSpacing="-0.02em"
                        >
                            My Goals
                        </Heading>
                        
                        <VStack spacing={3} w="full">
                            {goals.map((goal) => (
                                <Box
                                    key={goal.id}
                                    w="full"
                                    bg={goal.completed ? "whiteAlpha.50" : "whiteAlpha.100"}
                                    borderRadius="xl"
                                    p={4}
                                    cursor="pointer"
                                    onClick={() => toggleGoal(goal.id)}
                                    transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                    animation={goal.completed ? `${fadeComplete} 0.3s ease forwards` : undefined}
                                    _hover={{ bg: goal.completed ? "whiteAlpha.50" : "whiteAlpha.150" }}
                                    border="1px solid"
                                    borderColor={goal.completed ? "whiteAlpha.100" : "whiteAlpha.200"}
                                >
                                    <Flex align="center" gap={4}>
                                        <Box
                                            w={6}
                                            h={6}
                                            borderRadius="md"
                                            border="2px solid"
                                            borderColor={goal.completed ? "#4A90E2" : "whiteAlpha.400"}
                                            bg={goal.completed ? "#4A90E2" : "transparent"}
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            transition="all 0.2s"
                                            flexShrink={0}
                                        >
                                            {goal.completed && (
                                                <Icon 
                                                    as={FaCheck} 
                                                    w={3} 
                                                    h={3} 
                                                    color="white"
                                                    animation={`${checkBounce} 0.3s ease`}
                                                />
                                            )}
                                        </Box>
                                        <VStack align="flex-start" spacing={1} flex="1">
                                            <Text
                                                fontSize="md"
                                                fontWeight="400"
                                                position="relative"
                                                color={goal.completed ? "whiteAlpha.500" : "white"}
                                                transition="color 0.3s"
                                            >
                                                {goal.text}
                                                {goal.completed && (
                                                    <Box
                                                        position="absolute"
                                                        top="50%"
                                                        left="0"
                                                        h="1px"
                                                        bg="whiteAlpha.500"
                                                        animation={`${strikethrough} 0.3s ease forwards`}
                                                    />
                                                )}
                                            </Text>
                                            <HStack spacing={2}>
                                                <Icon
                                                    as={goal.setBy === "doctor" ? FaUserMd : FaUser}
                                                    w={3}
                                                    h={3}
                                                    color={goal.setBy === "doctor" ? "#4A90E2" : "whiteAlpha.500"}
                                                />
                                                <Text
                                                    fontSize="xs"
                                                    color={goal.setBy === "doctor" ? "#4A90E2" : "whiteAlpha.500"}
                                                    letterSpacing="0.05em"
                                                    textTransform="uppercase"
                                                >
                                                    {goal.setBy === "doctor" ? "Set by My Doctor" : "Set by Me"}
                                                </Text>
                                            </HStack>
                                        </VStack>
                                    </Flex>
                                </Box>
                            ))}
                        </VStack>

                        <Button
                            variant="ghost"
                            size="sm"
                            color="whiteAlpha.600"
                            fontWeight="400"
                            letterSpacing="0.05em"
                            textTransform="uppercase"
                            fontSize="xs"
                            _hover={{ color: "#4A90E2" }}
                            px={0}
                        >
                            + Add New Goal
                        </Button>
                    </VStack>
                </Flex>
            </Container>
        </Box>
    );
};

export default MemberHomepage;
